import React, { ChangeEvent, ReactNode } from 'react';
import { MdClose, MdWarning } from 'react-icons/md';
import RadioButton from '../RadioButton/RadioButton';
import { t } from 'i18next';
import styles from './FeedbackComposables.module.scss';

type FeedbackContainerType = {
  children: ReactNode;
  isModalOpen?: boolean;
};

type FeedbackContentType = {
  subHeading: string;
  inputPlaceholder: string;
  response: string | null;
  isNegativeFeedback?: boolean;
  selectedRadioInput?: string | null;
  showError?: boolean;
  handleFeedbackResponse?: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRadioSelect?: (selection: string) => void;
};

type FeebackFooterType = {
  handleCancel?: () => void;
  handleSubmit?: () => void;
  handleSkip?: () => void;
};

type FeedbackHeaderType = {
  title: string;
  handleClose?: () => void;
};

const FEEDBACK_LABELS = [
  'IT_WASNT_RELEVANT',
  'IT_WASNT_ACCURATE',
  'IT_WAS_BIASED',
  'IT_WAS_UNSAFE',
];

const FeedbackContainer = (props: FeedbackContainerType) => {
  const { children, isModalOpen } = props;

  if (!isModalOpen) return null;

  return (
      <div className={styles.scrim}>
        <div
          className={styles.modal}
          role='dialog'
          aria-modal='true'
        >
          {children}
        </div>
      </div>
  );
};

const Header = (props: FeedbackHeaderType) => {
  const { title, handleClose } = props;

  return (
    <div className={styles.headerRow}>
      <h2 className={styles.modalTitle}>{title}</h2>
      <button
        className={styles.closeBtn}
        onClick={handleClose}
        aria-label={t('CLOSE_MODAL')}
      >
        <MdClose size='1.5em' />
      </button>
    </div>
  );
};

const Content = (props: FeedbackContentType) => {
  const {
    response,
    subHeading,
    inputPlaceholder,
    isNegativeFeedback,
    selectedRadioInput,
    showError,
    handleRadioSelect,
    handleFeedbackResponse,
  } = props;

  return (
    <div className={styles.contentArea} tabIndex={-1}>
      {/* Sub-heading */}
      <p className={styles.subHeading}>{subHeading}</p>

      {/* Radio options — negative feedback only */}
      {isNegativeFeedback && (
        <fieldset className={styles.radioFieldset} aria-label={subHeading}>
          {FEEDBACK_LABELS.map((label, index) => (
            <div className={styles.radioRow} key={index}>
              <RadioButton
                label={t(label)}
                inputClassName=''
                labelClassName=''
                handleRadioSelect={() => handleRadioSelect?.(label)}
                isChecked={label === selectedRadioInput}
              />
            </div>
          ))}
        </fieldset>
      )}

      {/* Error banner — shown after submit attempt with no radio selected */}
      {showError && (
        <div
          className={styles.errorBanner}
          role='alert'
          aria-live='assertive'
        >
          <div className={styles.errorContainer}>
            <MdWarning className={styles.errorIcon} aria-hidden='true' />
            <span>{t('FEEDBACK_SELECT_OPTION_ERROR')}</span>
          </div>
        </div>
      )}

      {/* Open-ended text input */}
      <input
        className={styles.feedbackInput}
        placeholder={inputPlaceholder}
        onChange={handleFeedbackResponse}
        value={response ?? ''}
      />
    </div>
  );
};

const Footer = (props: FeebackFooterType) => {
  const { handleCancel, handleSubmit, handleSkip: _handleSkip } = props;

  return (
    <div className={styles.footerRow}>
      <button className={styles.noThanksBtn} onClick={handleCancel}>
        {t('NO_THANKS')}
      </button>
      <button className={styles.submitBtn} onClick={handleSubmit}>
        {t('SUBMIT')}
      </button>
    </div>
  );
};

FeedbackContainer.Header = Header;
FeedbackContainer.Content = Content;
FeedbackContainer.Footer = Footer;

export default FeedbackContainer;
